import FiltersContainer from '@features/filters/containers/FiltersContainer';
import { Search } from '@mui/icons-material';
import { Box, Container, CssBaseline, IconButton, Tooltip } from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import AccordionBusinessGroups from '../AccordionBusinessGroups/AccordionBusinessGroups';
import useBusinessGroupsContainerStyles from './BusinessGroups.styles';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import AssociateClient from '../AssociateClient/AssociateClient';
import { useSnackbar } from 'notistack';
import useRequests from '@hooks/requests.hook';
import { Grupos } from '@features/customers/interfaces/businessGroups.interfaces';
import FormFilter from '@features/filters/interfaces/business-groups.interface';
import SightLoading from '@features/common/Loading/SightLoading';

const BusinessGroups: React.FC = () => {
	const classes = useBusinessGroupsContainerStyles();
	const { control, ...form } = useForm<FormFilter>({
		defaultValues: {
			group: '',
			identification: '',
			name: '',
		},
	});
	const [isLoading, setIsLoading] = useState(false);
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [openAssociateClient, setOpenAssociateClient] = useState(false);
	const [groups, setGroups] = useState<Grupos[]>([]);
	const [allGroups, setAllGroups] = useState<Grupos[]>([]);
	const { enqueueSnackbar } = useSnackbar();
	const { requestBackendSuite } = useRequests();
	const [filter, setFilter] = useState(false);
	const [disabled, setDisabled] = useState<'name' | 'identification' | false>(false);
	const handleClickOpen = () => {
		setOpenAssociateClient(true);
	};

	const handleClose = () => {
		setOpenAssociateClient(false);
		getGroups();
	};

	useEffect(() => {
		getGroups();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const getGroups = async () => {
		try {
			setIsLoading(true);
			const response: any = await requestBackendSuite('grupos-empresariales/', 'GET');
			if (response.isError) {
				throw new Error(response.message);
			}
			setAllGroups(response.data);
			setGroups(response.data);
			setIsLoading(false);
		} catch (error: any) {
			setIsLoading(false);
			if (typeof error === 'string') {
				enqueueSnackbar(error, { variant: 'error' });
			} else if (error.response && error.response.data.message) {
				enqueueSnackbar(error.response.data.message, { variant: 'error' });
			}
		}
	};

	const onClear = () => {
		if (
			!form.formState.dirtyFields.name &&
			!form.formState.dirtyFields.identification &&
			!form.formState.dirtyFields.name
		) {
			setDisabled(false);
			setGroups(allGroups);
			setFilter(false);
		}
	};
	const search = () => {
		const data = form.getValues();
		if (data.group === '' && data.identification === '' && data.name === '') {
			setDisabled(false);
			setGroups(allGroups);
			setFilter(false);
			return;
		}
		if (data.identification && !data.name) {
			setDisabled('name');
		} else if (!data.identification && data.name) {
			setDisabled('identification');
		} else {
			setDisabled(false);
		}
		const groups = allGroups
			.filter((i) => {
				if (
					data.group !== '' &&
					i.descripcion.toLowerCase().includes(data.group.toLowerCase())
				) {
					return true;
				} else {
					const clients = i.clientes_grupo.filter((c) => {
						if (data.identification !== '' && data.name) {
							if (
								data.identification !== '' &&
								c.identificacion_cliente
									.toLowerCase()
									.includes(data.identification.toLowerCase()) &&
								data.name !== '' &&
								c.nombre.toLowerCase().includes(data.name.toLowerCase())
							) {
								return true;
							} else {
								return false;
							}
						} else {
							if (
								(data.identification !== '' &&
									c.identificacion_cliente
										.toLowerCase()
										.includes(data.identification.toLowerCase())) ||
								(data.name !== '' &&
									c.nombre.toLowerCase().includes(data.name.toLowerCase()))
							) {
								return true;
							} else {
								return false;
							}
						}
					});
					if (clients.length > 0) {
						return true;
					} else {
						return false;
					}
				}
			})
			.map((i) => {
				return {
					...i,
					open: true,
					clientes_grupo: i.clientes_grupo.map((c) => {
						if (data.identification !== '' && data.name) {
							if (
								data.identification !== '' &&
								c.identificacion_cliente
									.toLowerCase()
									.includes(data.identification.toLowerCase()) &&
								data.name !== '' &&
								c.nombre.toLowerCase().includes(data.name.toLowerCase())
							) {
								return {
									...c,
									background: true,
								};
							} else {
								return {
									...c,
									background: false,
								};
							}
						} else {
							if (
								(data.identification !== '' &&
									c.identificacion_cliente
										.toLowerCase()
										.includes(data.identification.toLowerCase())) ||
								(data.name !== '' &&
									c.nombre.toLowerCase().includes(data.name.toLowerCase()))
							) {
								return {
									...c,
									background: true,
								};
							} else {
								return {
									...c,
									background: false,
								};
							}
						}
					}),
				};
			});
		setFilter(true);
		setGroups(groups);
	};
	useEffect(() => {
		if (document.getElementsByClassName('searched').length > 0) {
			document.getElementsByClassName('searched')[0].scrollIntoView();
		}
	}, [groups]);

	return (
		<div className={classes.root}>
			{isLoading && <SightLoading show={true} />}
			<CssBaseline />
			<Container maxWidth="lg">
				<Box className={classes.box}>
					<div className={classes.containerTitle}>
						<h2
							style={{ marginTop: 25, marginBottom: 0, width: 'auto', float: 'left' }}
						>
							Grupos empresariales
						</h2>
						<Tooltip
							classes={{ tooltip: classes.tooltip, arrow: classes.arrow }}
							style={{
								color: '#003C82',
								marginBottom: '2%',
								marginTop: '6%',
								marginLeft: 15,
							}}
							title="Asociar clientes"
						>
							<IconButton onClick={handleClickOpen}>
								<GroupAddIcon style={{ fontSize: '28px' }} />
							</IconButton>
						</Tooltip>
					</div>

					<div className={isOpen ? classes.filtersOpen : classes.filters}>
						<FiltersContainer
							counter={Object.keys(form.formState.dirtyFields).length ?? 0}
							id="groups-filter"
							inputs={[
								{
									control: control,
									defaultValue: '',
									name: 'group',
									placeholder: 'Escribe el grupo',
									startAdorment: <Search fontSize="small" />,
									width: '14.3rem',
									onClear,
									onKeyPress: (e) => {
										if (e.key === 'Enter') {
											search();
										}
									},
								},
								{
									control: control,
									defaultValue: '',
									name: 'identification',
									placeholder: 'Escribe el N° identificación',
									startAdorment: <Search fontSize="small" />,
									width: '14rem',
									onClear,
									disabled: disabled === 'identification',
									onKeyPress: (e) => {
										if (e.key === 'Enter') {
											search();
										}
									},
								},
								{
									control: control,
									defaultValue: '',
									name: 'name',
									placeholder: 'Escribe la razón social',
									startAdorment: <Search fontSize="small" />,
									width: '12.95rem',
									disabled: disabled === 'name',
									onClear,
									onKeyPress: (e) => {
										if (e.key === 'Enter') {
											search();
										}
									},
								},
							]}
							isOpen={isOpen}
							setIsOpen={setIsOpen}
							name={isOpen ? null : 'Filtros'}
						/>
					</div>
					<AccordionBusinessGroups filter={filter} getGroups={getGroups} data={groups} />
					<div className={classes.paginator}></div>
				</Box>
			</Container>
			<div>
				{openAssociateClient ? (
					<AssociateClient
						handleClose={handleClose}
						open={openAssociateClient}
					></AssociateClient>
				) : null}
			</div>
		</div>
	);
};
export default BusinessGroups;
